import React from 'react';
import { Link } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, RECAPTCHA_ENABLED } from 'config';
import { RouteComponentProps } from 'react-router';
import { StoreAction } from 'types';
import { PASSWORD_RESET, DASHBOARD, SIGNUP, AUTHORIZE } from 'constants/routes';
import Services from 'services';
import classnames from 'classnames';

import Text, { Heading, Subtitle } from 'components/Text';
import { t, getTranslation } from 'components/Translate';
import Input from 'components/Input/Base';
import Button from 'components/Button';
import Alert from 'components/Alert';
import Icon from 'components/Icon';
import Recaptcha from 'components/Recaptcha';
import TwoFactor from 'components/TwoFactor';

import styles from './Onboarding.module.scss';

type Props = {
  userChecked: StoreAction<'userChecked'>,
};
type State = {
  step: 'form' | '2fa',
  email: string,
  password: string,
  isLoading: boolean,
  error: string | null,
  isSuccess: boolean,
};

class Login extends React.Component<Props & RouteComponentProps, State> {
  private recaptcha = React.createRef<Recaptcha>();

  state: State = {
    step: 'form',
    email: '',
    password: '',
    isLoading: false,
    error: null,
    isSuccess: false,
  };

  private onForgot = () => this.props.history.push(PASSWORD_RESET);
  private onSubmit = async (otpAttempt: string | null = null) => {
    if (!this.validate() || (RECAPTCHA_ENABLED && !this.recaptcha.current)) return;

    this.setState({ isLoading: true, error: null });
    this.props.userChecked(false);
    try {
      let token;
      if (RECAPTCHA_ENABLED && this.recaptcha.current) {
        token = await this.recaptcha.current.execute();
      }

      const result = await Services.get('auth').login({
        email: this.state.email,
        password: this.state.password,
        otpAttempt: otpAttempt !== null ? otpAttempt : undefined,
        token,
      });

      if (this.recaptcha.current) this.recaptcha.current.reset();

      if (result.deviceToken) {
        this.props.history.push(AUTHORIZE, { email: this.state.email });
      } else if (result.otpEnabled) {
        this.setState({ step: '2fa', isLoading: false });
      } else {
        this.setState({ isSuccess: true });
        this.props.history.push(DASHBOARD);
      }
    } catch (e) {
      this.setState({ isLoading: false, error: e.message });
      if (this.recaptcha.current) this.recaptcha.current.reset();
    }
  };
  private on2FA = (code: string) => this.onSubmit(code);

  private validate(): boolean {
    if (!this.state.email.length) {
      this.setState({ error: getTranslation('onboarding.error.emailRequired') });
      return false;
    }

    if (this.state.password.length < PASSWORD_MIN_LENGTH) {
      this.setState({ error: getTranslation('onboarding.error.passwordLength', { length: PASSWORD_MIN_LENGTH }) });
      return false;
    }

    if (RECAPTCHA_ENABLED && !this.recaptcha.current) {
      this.setState({ error: 'Recaptcha error!' });
      return false;
    }

    return true;
  }

  render() {
    const { step } = this.state;

    return (
      <div className={ classnames(styles.center, 't-center') }>
        { step === '2fa' ? this.render2fa() : this.renderForm() }

        { (RECAPTCHA_ENABLED && !this.state.isSuccess) && <Recaptcha ref={ this.recaptcha } /> }
      </div>
    );
  }
  private renderForm() {
    return (
      <>
        <div className={ styles.width }>
          <Heading type="large" className={ styles.title }>{ t('onboarding.login.title') }</Heading>

          <Input
            className={ classnames(styles.firstInput, styles.noTransition) }
            value={ this.state.email }
            onChange={ (email) => this.setState({ email }) }
            placeholder={ getTranslation('common.label.email') }
            fullWidth={ true }
            size="large"
            onEnter={ this.onSubmit }
            attrs={ {
              autoFocus: true,
              type: 'email',
              formNoValidate: true,
            } }
          />
          <Input
            className={ classnames(styles.passwordInput, styles.noTransition) }
            value={ this.state.password }
            onChange={ (password) => this.setState({ password }) }
            placeholder={ getTranslation('common.label.password') }
            fullWidth={ true }
            size="large"
            // mark as required for styling when has content
            attrs={ { type: 'password', required: true } }
            onEnter={ this.onSubmit }
          />

          <div className="mt-2 mb-4 t-right" onClick={ this.onForgot }>
            <Text color="dark-light" className="cursor--pointer">
              <Icon.Inlined name="lock" color="dark-light" size="x-small" className="mr-1" />
              { t('onboarding.login.reset') }
            </Text>
          </div>

          <div className={ styles.error }>
            { this.state.error &&
              <Alert theme="error" isCloseable={ false }>
                <span dangerouslySetInnerHTML={ { __html: this.state.error } } />
              </Alert>
            }
          </div>

          <Button
            className={ styles.buttonAction }
            fullWidth={ true }
            size="large"
            isLoading={ this.state.isLoading }
            loadingText={ getTranslation('common.label.verifying') }
            onClick={ this.onSubmit }
          >
            { t('common.action.login') }
          </Button>

          <div className="my-4">
            <Subtitle type="small" color="dark-light">
              { t('onboarding.header.dontHave') }
              &nbsp;
              <Link className="text-weight--medium text-clip" to={ SIGNUP }>
                { t('common.action.createAccount') }
              </Link>
            </Subtitle>
          </div>
        </div>
      </>
    );
  }
  private render2fa() {
    return (
      <TwoFactor
        className={ classnames(styles.widthBigger, styles.twoFactor) }
        hasIcon={ false }
        descriptionKey="onboarding.twoFactor.description"
        onSubmit={ this.on2FA }
        isLoading={ this.state.isLoading }
        error={ this.state.error }
        onErrorDismiss={ () => this.setState({ error: null }) }
        isOverlaying={ false }
      />
    );
  }
}

export default Login;
